/** Last N texts for a scheduled message with Firetext delivery status (delivered / awaiting receipt / failed / skipped). */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, Loader2, MinusCircle, RefreshCw } from 'lucide-react';
import API_URL from '@/config/api';

const TONE = {
  ok: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  wait: { cls: 'bg-amber-50 text-amber-800 border-amber-200', Icon: Clock },
  bad: { cls: 'bg-rose-50 text-rose-800 border-rose-200', Icon: AlertTriangle },
  off: { cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: MinusCircle },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export function TextDeliveryLog({ jobKey, limit = 10 }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/message-centre/schedules/${jobKey}/log?limit=${limit}`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      setRows(r.ok ? d.rows || [] : []);
    } finally { setBusy(false); }
  }, [jobKey, limit]);
  useEffect(() => { if (open && rows === null) load(); }, [open, rows, load]);
  const failed = (rows || []).filter((x) => x.tone === 'bad').length;
  return (
    <div className="mt-3 pt-3 border-t" data-testid={`sched-log-${jobKey}`}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-bold text-gray-700 inline-flex items-center gap-1 hover:text-violet-700" data-testid={`sched-log-toggle-${jobKey}`}>
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Text delivery log
          <span className="font-normal text-gray-400">· last {limit}</span>
          {rows && failed > 0 && <span className="ml-1 rounded-full bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[10px] font-bold" data-testid={`sched-log-failed-${jobKey}`}>{failed} failed</span>}
        </button>
        {open && <button type="button" onClick={load} disabled={busy} className="text-gray-400 hover:text-gray-700 disabled:opacity-50" aria-label="Refresh log" data-testid={`sched-log-refresh-${jobKey}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</button>}
      </div>
      {open && (
        <div className="mt-2">
          {rows === null ? <div className="text-xs text-gray-400 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Loading…</div>
            : !rows.length ? <div className="text-xs text-gray-400 italic py-1" data-testid={`sched-log-empty-${jobKey}`}>No texts sent yet</div>
              : (
                <ul className="divide-y rounded-lg border bg-gray-50/60">
                  {rows.map((r) => { const t = TONE[r.tone] || TONE.wait; return (
                    <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs" data-testid={`sched-log-row-${jobKey}`}>
                      <span className="text-gray-500 w-[120px] shrink-0">{fmt(r.at)}</span>
                      <span className="font-semibold text-gray-800 min-w-[140px]">{r.name || r.to}{r.name && <span className="font-normal text-gray-400 ml-1">{r.to}</span>}</span>
                      {r.test && <span className="rounded bg-violet-100 text-violet-800 px-1.5 py-0.5 text-[10px] font-bold">TEST</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${t.cls}`} data-testid={`sched-log-status-${jobKey}`}><t.Icon className="w-3 h-3" /> {r.label}</span>
                      {r.error && r.tone === 'bad' && <span className="text-rose-600 truncate max-w-[320px]" title={r.error}>{r.error}</span>}
                      {r.delivered_at && r.tone === 'ok' && <span className="text-gray-400">at {r.delivered_at}</span>}
                    </li>); })}
                </ul>
              )}
          <p className="text-[10px] text-gray-400 mt-1.5">"Awaiting receipt" means Firetext accepted the text; it flips to Delivered when the network confirms (needs the Firetext delivery-receipt webhook pointed at this portal).</p>
        </div>
      )}
    </div>
  );
}
